import { defaultAutoLayoutFrame, defaultAutoLayoutItem } from '../../auto-layout.js';
import { fluidItemStyles, resizeFluidItems } from '../../fluid-layout.js';
import type { WebEditorLayoutProject } from '../../layout.js';
import type { WebEditorElementDefinition } from '../../element-definition.js';
import { editorNodeIdClass } from './dom.js';
import { queryEditorPreviewNode } from './editorPreviewViewport.js';

export interface Options<P extends WebEditorLayoutProject = WebEditorLayoutProject> {
  project: P;
  definitions: readonly WebEditorElementDefinition[];
  cloneProject: (project: P) => P;
  updateProject: (project: P) => void;
}
const nodeFor = (id: string) => queryEditorPreviewNode(`.vp-editor-preview-page .${editorNodeIdClass(id)}`);

/** Resize in document flow. Preview touches DOM only; pointer-up creates one undoable project update. */
export const prepareFluidImageResize = <P extends WebEditorLayoutProject>(options: Options<P>, imageId: string) => {
  const imageDefinition = options.definitions.find(item => item.id === imageId);
  if (imageDefinition?.kind !== 'image') return null;
  const hostId = imageDefinition.maskHostId ?? imageId;
  const hostDefinition = options.definitions.find(item => item.id === hostId);
  const parentId = hostDefinition?.parentId;
  if (!parentId) return null;
  const host = nodeFor(hostId), parent = nodeFor(parentId);
  if (!host || !parent || host.parentElement !== parent) return null;
  const siblings = options.definitions.filter(item => item.parentId === parentId).flatMap(item => {
    const node = nodeFor(item.id);
    return node && node.parentElement === parent ? [{ id: item.id, node }] : [];
  });
  const index = siblings.findIndex(item => item.id === hostId);
  if (index < 0) return null;
  const style = getComputedStyle(parent);
  const gap = parseFloat(style.columnGap) || 0;
  const gapY = parseFloat(style.rowGap) || gap;
  const paddingX = parseFloat(style.paddingLeft) || 0;
  const available = Math.max(24, parent.clientWidth - paddingX * 2 - gap * Math.max(0, siblings.length - 1));
  const startWidth = host.offsetWidth, startHeight = host.offsetHeight;
  const baseWidths = siblings.map(item => options.project.elementFrames[item.id]?.fluidSize?.width ?? item.node.offsetWidth);
  const parentFrame = options.project.elementFrames[parentId];
  // A narrow stacked preview must not erase the established desktop proportions.
  const hostRect = host.getBoundingClientRect();
  const stacked = siblings.length > 1 && siblings.some(item => {
    const rect = item.node.getBoundingClientRect();
    return rect.top >= hostRect.bottom - 1 || rect.bottom <= hostRect.top + 1;
  });
  const snapshots = [parent, ...siblings.map(item => item.node)].map(node => ({ node, style: node.getAttribute('style'), className: node.className }));
  let next = options.cloneProject(options.project);
  const restore = () => snapshots.forEach(({ node, style: originalStyle, className }) => {
    node.className = className;
    if (originalStyle === null) node.removeAttribute('style'); else node.setAttribute('style', originalStyle);
  });
  const update = (requestedWidth: number, requestedHeight: number) => {
    const width = Math.max(24, Math.min(2400, requestedWidth));
    const height = Math.max(24, Math.min(2400, requestedHeight));
    const weights = stacked
      ? baseWidths.map((value, position) => position === index ? value * width / Math.max(1, startWidth) : value)
      : resizeFluidItems(baseWidths, index, width, available);
    next = options.cloneProject(options.project);
    next.elementFrames[parentId] = {
      ...(parentFrame ?? { x: 0, y: 0 }), x: 0, y: 0, fluidLayout: true,
      autoLayoutDesktop: parentFrame?.autoLayoutDesktop ?? { ...defaultAutoLayoutFrame('wrap'), gapX: gap, gapY, paddingX, alignY: 'center' },
      autoLayoutMobile: parentFrame?.autoLayoutMobile ?? { ...defaultAutoLayoutFrame('wrap'), gapX: Math.min(24, gap), gapY, paddingX, alignY: 'center' },
    };
    parent.classList.add('vp-fluid-layout');
    siblings.forEach((item, position) => {
      const frame = next.elementFrames[item.id] ?? { x: 0, y: 0 };
      const itemWidth = weights[position] ?? 24;
      const previousSize = frame.fluidSize;
      const fluidSize = {
        width: itemWidth,
        maxWidthPercent: position === index ? (stacked ? Math.min(100, width / Math.max(1, parent.clientWidth - paddingX * 2) * 100) : 100) : previousSize?.maxWidthPercent ?? 100,
        ...(position === index ? { height: itemWidth * height / width } : previousSize?.height ? { height: itemWidth * previousSize.height / previousSize.width } : {}),
      };
      next.elementFrames[item.id] = {
        ...frame, x: 0, y: 0, fluidSize,
        autoLayoutItemDesktop: frame.autoLayoutItemDesktop ?? { ...defaultAutoLayoutItem(position), widthMode: 'fill' },
        autoLayoutItemMobile: frame.autoLayoutItemMobile ?? { ...defaultAutoLayoutItem(position), widthMode: 'fill' },
      };
      item.node.classList.add('vp-fluid-item');
      if (fluidSize.height) item.node.classList.add('vp-fluid-media');
      for (const entry of fluidItemStyles(fluidSize)) {
        const colon = entry.indexOf(':');
        item.node.style.setProperty(entry.slice(0, colon), entry.slice(colon + 1).replace(/;$/u, '').trim());
      }
    });
    const image = next.elementFrames[imageId] ?? { x: 0, y: 0 };
    next.elementFrames[imageId] = { ...image, objectFit: image.objectFit ?? 'cover', ...(!imageDefinition.maskHostId ? { imageMaskDesktop: true, imageMaskMobile: true } : {}) };
  };
  return { host, startWidth, startHeight, update, restore, commit: () => { restore(); options.updateProject(next); } };
};
